/** The 90-day blocked-volume trend (L1) — the history the deck has never had.
 *
 *  A trend over time is a line (dashboard design rules §2). Two series, so the
 *  legend is mandatory (§6): blocked, and the dangerous mail that got past the
 *  control. They share one y-axis on purpose — a dual axis is banned, and the
 *  point of the pair is that delivered should be invisibly small next to
 *  blocked. If it is not, that is the finding.
 *
 *  Gaps stay gaps. The backend omits days it never collected rather than
 *  zero-filling them, and `connectNulls` is off, so a collection outage renders
 *  as a break in the line instead of as a quiet week (§5, "no data ≠ zero").
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { type DataGridColumnDef, EmptyState } from "@norican/react";

import { ChartCard, type Verdict } from "@/components/board";
import type { SecurityTrendPoint } from "@/lib/api";
import { formatCountCompact } from "@/lib/charts/config";

import { TREND_ANCHOR } from "./exec-tiles";
import { DIRECTION_COLOR, EXEC_STALE_AFTER_MS, formatCount, formatDayLabel } from "./status";

/** `?data=blocked-trend` opens this chart's table twin. */
const TWIN_ID = "blocked-trend";

/**
 * Status colours, not categorical ones: these two series are "the control
 * worked" and "the control failed", which is exactly what status tones mean.
 */
const TREND_CONFIG: ChartConfig = {
  blocked: { label: "Blocked before delivery", color: DIRECTION_COLOR.blocked },
  delivered: { label: "Reached a mailbox", color: DIRECTION_COLOR.delivered },
};

interface BlockedTrendProps {
  points: SecurityTrendPoint[];
  windowDays: number;
  generatedAt: string | null | undefined;
}

export function BlockedTrend({ points, windowDays, generatedAt }: BlockedTrendProps) {
  const rows = points.map((point) => ({ ...point, label: formatDayLabel(point.day) }));
  const delivered = rows.reduce((sum, row) => sum + row.delivered, 0);

  const twinColumns: DataGridColumnDef<(typeof rows)[number]>[] = [
    { id: "day", header: "Day", accessor: (row) => row.day },
    {
      id: "blocked",
      header: "Blocked",
      numeric: true,
      accessor: (row) => row.blocked,
      cell: (row) => <span className="tabular-nums">{formatCount(row.blocked)}</span>,
    },
    {
      id: "delivered",
      header: "Reached a mailbox",
      numeric: true,
      accessor: (row) => row.delivered,
      cell: (row) => <span className="tabular-nums">{formatCount(row.delivered)}</span>,
    },
    {
      id: "clicks",
      header: "Clicks permitted",
      numeric: true,
      accessor: (row) => row.clicks_permitted,
      cell: (row) => <span className="tabular-nums">{formatCount(row.clicks_permitted)}</span>,
    },
    {
      id: "recipients",
      header: "Recipients",
      numeric: true,
      accessor: (row) => row.recipients,
      cell: (row) => <span className="tabular-nums">{formatCount(row.recipients)}</span>,
    },
  ];

  return (
    <section id={TREND_ANCHOR} className="scroll-mt-6">
      <ChartCard
        title="Dangerous emails blocked before delivery"
        caption={
          rows.length === 0
            ? `No days collected in the last ${windowDays}`
            : `${rows.length} of ${windowDays} days collected · gaps are days never collected, not quiet days`
        }
        since={generatedAt}
        staleAfterMs={EXEC_STALE_AFTER_MS}
        verdict={trendVerdict(rows.length, delivered)}
        tableTwin={{
          id: TWIN_ID,
          ariaLabel: "Dangerous email blocked and delivered, per day",
          data: rows,
          columns: twinColumns,
          getRowId: (row) => row.day,
        }}
      >
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No trend yet"
            description="The collector has not written a day of counts. Once it does, this is the first time the deck's number has a history."
          />
        ) : (
          <Chart.Container
            config={TREND_CONFIG}
            className="h-72"
            aria-label={`Dangerous email blocked and delivered per day over the last ${windowDays} days`}
          >
            <Chart.LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis
                {...Chart.axisProps}
                width={56}
                allowDecimals={false}
                tickFormatter={(value: number) => formatCountCompact(value)}
              />
              <Chart.Tooltip
                content={<Chart.TooltipContent formatter={(value) => formatCount(Number(value))} />}
              />
              <Chart.Legend content={<Chart.LegendContent />} />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="blocked"
                type="monotone"
                stroke="var(--color-blocked)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="delivered"
                type="monotone"
                stroke="var(--color-delivered)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </Chart.LineChart>
          </Chart.Container>
        )}
      </ChartCard>
    </section>
  );
}

/** The deck's own reading of this chart, in one line. */
function trendVerdict(days: number, delivered: number): Verdict {
  if (days === 0) {
    return {
      label: "No data",
      tone: "neutral",
      text: "Nothing collected yet, so there is no claim to make here.",
    };
  }
  if (delivered === 0) {
    return {
      label: "Holding",
      tone: "success",
      text: "Every dangerous message in this window was stopped before delivery. No decision needed.",
    };
  }
  return {
    label: "Watch",
    tone: "warning",
    text: `${formatCount(delivered)} dangerous messages reached a mailbox in this window — the red line is the one to read, not the green one.`,
  };
}
